import React, { useCallback, useEffect, useRef, useState } from 'react';
import supabase from '../config/supabase';
import { directQuery, directUpdate } from '../utils/directFetch';
import { recordPriceHistory } from '../utils/priceHistory';
import ImportApprovalTable from './ImportApprovalTable';
import { findCountTargetsForNewItem, assignItemToCountSessions } from '../utils/autoAssignToCounts';
import { buildCatalogLookup, classifyItem, overrideItemClassification } from '../utils/importDedup';
import { CLASSIFICATIONS } from '../utils/importLanding';
import { upsertManualOption, fetchOptionsForItems } from '../services/purchaseOptionService';
import { resolveVendorIdForStore } from '../utils/vendorResolution';
import { distinctVendorCount, activeOptions } from '../utils/itemCostSummary';
import { isIosShell } from '../utils/isIosShell';
import { visuallyHiddenFileInputStyle } from '../utils/visuallyHiddenFileInputStyle';
import { fileSignature } from '../utils/fileDedupe';
import { takeScanPhoto } from '../utils/scanPhoto';
import StagedFilePreview from './StagedFilePreview';
import {
  CANDIDATE_LIMIT,
  ACTIONS,
  getCandidates,
  scoreCandidacy,
  callPriceExtractWithMeta,
  callPriceMatch,
  summarizeInvoices,
  applyInvoiceFieldEdit,
  formatInvoiceDateLabel,
} from '../utils/priceUpdateMatching';

/**
 * Records an invoice line as a VENDOR PRODUCT on a canonical item — the
 * multi-vendor half of a price update.
 *
 * Before this existed a second supplier's invoice overwrote the item's single
 * price, erasing what the first supplier charged. Now each vendor's pack and
 * price live on their own purchase_options row, keyed by vendor + SKU + pack,
 * so re-importing the same supplier updates their row and a new supplier adds
 * one. The item's count unit is untouched.
 *
 * Best-effort: a failure here is logged and returns null rather than aborting
 * the price update the user asked for.
 */
async function writeVendorProduct({ storeId, inventoryItemId, item, effectiveDate }) {
  if (!storeId || !inventoryItemId) return null;
  const price = parseFloat(item?.case_price);
  if (!Number.isFinite(price) || price <= 0) return null;

  try {
    let vendorId = null;
    const vendorName = (item?.vendor || '').trim();
    if (vendorName) {
      try {
        vendorId = await resolveVendorIdForStore({
          supabaseClient: supabase, storeId, vendorName,
        });
      } catch (_) {
        vendorId = null;
      }
    }

    const caseQty = parseFloat(item?.case_quantity);
    return await upsertManualOption(storeId, inventoryItemId, {
      vendor_id: vendorId || null,
      vendor_description: vendorName || null,
      vendor_sku: item?.supc || item?.item_code || null,
      purchase_unit: item?.unit || 'case',
      pack_description: item?.pack_size || null,
      qty_per_package: Number.isFinite(caseQty) && caseQty > 0 ? caseQty : 1,
      package_price: price,
      effective_date: effectiveDate || null,
      // Read off a supplier document, but not yet reconciled against a
      // received delivery — the verified-cost gate decides what that is worth.
      verification_status: 'unverified',
    });
  } catch (err) {
    console.warn('[UpdatePrices] Vendor product write failed (non-fatal):', err?.message);
    return null;
  }
}

const ACCEPTED_TYPES = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.pdf', '.csv', '.xlsx', '.xls'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 10;

const ACTION_LABELS = {
  [ACTIONS.UPDATE_ALL]: 'Update All',
  [ACTIONS.UPDATE_PRICE]: 'Update Price Only',
  [ACTIONS.IGNORE]: 'Ignore',
  [ACTIONS.ADD_NEW]: 'Add as New Item',
};

// Candidate scoring (tokenize/jaccard/scoreCandidacy/getCandidates) and the
// price-update-ai edge calls live in ../utils/priceUpdateMatching so this page
// and Counter Intelligence's in-chat price update share one processor.

const ACTION_COLORS = {
  [ACTIONS.UPDATE_ALL]: '#5A4A42',
  [ACTIONS.UPDATE_PRICE]: '#6B8E6B',
  [ACTIONS.IGNORE]: '#8B7B73',
  [ACTIONS.ADD_NEW]: '#7B6B9A',
};

function getImageMime(fileName) {
  const n = (fileName || '').toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.heic') || n.endsWith('.heif')) return 'image/heic';
  return 'image/jpeg';
}

function isImage(file) {
  const n = (file?.name || '').toLowerCase();
  return ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'].some(e => n.endsWith(e));
}

function isAccepted(file) {
  const n = (file?.name || '').toLowerCase();
  return ACCEPTED_TYPES.some(e => n.endsWith(e));
}

async function readImageBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result !== 'string' || !result.includes(',')) {
        reject(new Error('Cannot read image'));
        return;
      }
      resolve({ mediaType: (file.type || '').trim() || getImageMime(file.name), data: result.split(',')[1] });
    };
    reader.onerror = () => reject(new Error('Error reading image'));
    reader.readAsDataURL(file);
  });
}

async function extractPdfText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        if (typeof pdfjsLib === 'undefined') { resolve(''); return; }
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
        const pdf = await pdfjsLib.getDocument(new Uint8Array(e.target.result)).promise;
        let allText = '';
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          const tc = await page.getTextContent();
          allText += tc.items.map(i => i.str).join(' ') + '\n';
        }
        resolve(allText);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Error reading PDF'));
    reader.readAsArrayBuffer(file);
  });
}

async function extractCsvText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result || '');
    reader.onerror = () => reject(new Error('Error reading CSV'));
    reader.readAsText(file);
  });
}

async function extractExcelText(file) {
  return new Promise((resolve, reject) => {
    if (typeof XLSX === 'undefined') { reject(new Error('SheetJS not loaded')); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_csv(sheet));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Error reading Excel'));
    reader.readAsArrayBuffer(file);
  });
}

async function getEdgeHeaders() {
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
        apikey: window.SUPABASE_KEY,
        'Content-Type': 'application/json',
      };
    }
  } catch (_) {}
  // Fallback: read from localStorage (same pattern as FileUpload)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) {
        return {
          Authorization: `Bearer ${parsed.access_token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    } catch (_) {}
  }
  throw new Error('Not authenticated. Please sign in and try again.');
}

// Returns { items, invoiceDate, invoiceNumber } — the invoice date/number are
// used to date and reference the resulting price events.
const callEdgeExtract = (payload) => callPriceExtractWithMeta(getEdgeHeaders, payload);
const callEdgeMatch = (invoiceItem, candidates) => callPriceMatch(getEdgeHeaders, invoiceItem, candidates);

async function extractInvoiceItems(file, onStatus) {
  onStatus(`Extracting items from ${file.name}…`);
  const n = (file?.name || '').toLowerCase();

  if (isImage(file)) {
    const { mediaType, data } = await readImageBase64(file);
    return callEdgeExtract({ fileType: 'image', mediaType, imageData: data, fileName: file.name });
  } else if (n.endsWith('.pdf')) {
    const text = await extractPdfText(file);
    return callEdgeExtract({ fileType: 'pdf', text, fileName: file.name });
  } else {
    let text = '';
    if (n.endsWith('.csv')) text = await extractCsvText(file);
    else text = await extractExcelText(file);
    return callEdgeExtract({ fileType: 'csv', text, fileName: file.name });
  }
}

// Confirmation header above the approval list: which invoice(s) these lines came
// from — vendor and invoice date are editable so the user can fix a misread
// supplier or date before approving. The vendor is recorded with each price
// event (and applied to any new items); the date sets each item's price-change
// date (changed_at). Invoice number is shown read-only for reference.
function InvoiceSummaryBanner({ items, onEditInvoice }) {
  const invoices = summarizeInvoices(items);
  if (!invoices.length) return null;
  const fieldLabel = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: '#8B7B73', display: 'block', marginBottom: '0.15rem' };
  const fieldInput = { fontFamily: "'Open Sans', sans-serif", fontSize: '0.9rem', color: '#3F352E', background: '#fff', border: '1px solid #E0D3C4', borderRadius: 8, padding: '0.4rem 0.55rem', width: '100%', boxSizing: 'border-box' };
  return (
    <div style={{
      background: '#FFFDF9', border: '1px solid #E8DBCE', borderRadius: 12,
      padding: '0.85rem 1rem', marginBottom: '1rem', color: '#3F352E',
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#8B7B73' }}>
        {invoices.length === 1 ? 'Invoice' : `${invoices.length} invoices`}
      </div>
      {invoices.map((inv, i) => (
        <div key={inv.sourceFile ?? i} style={{ marginTop: i === 0 ? '0.5rem' : '0.75rem', paddingTop: i === 0 ? 0 : '0.65rem', borderTop: i === 0 ? 'none' : '1px dashed #EEE6DC' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem', alignItems: 'flex-end' }}>
            <label style={{ flex: '2 1 220px', minWidth: 0 }}>
              <span style={fieldLabel}>Vendor</span>
              <input
                type="text"
                value={inv.vendor || ''}
                placeholder="Vendor name"
                aria-label="Invoice vendor"
                onChange={(e) => onEditInvoice?.(inv, { vendor: e.target.value })}
                style={fieldInput}
              />
            </label>
            <label style={{ flex: '1 1 160px', minWidth: 0 }}>
              <span style={fieldLabel}>Invoice date</span>
              <input
                type="date"
                value={inv.invoiceDate || ''}
                aria-label="Invoice date"
                onChange={(e) => onEditInvoice?.(inv, { invoiceDate: e.target.value })}
                style={fieldInput}
              />
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 0.9rem', fontSize: '0.76rem', color: '#8B7B73', marginTop: '0.4rem' }}>
            {inv.invoiceNumber && <span>Invoice #{inv.invoiceNumber}</span>}
            <span>{inv.invoiceDate ? formatInvoiceDateLabel(inv.invoiceDate) : 'No invoice date — prices dated by import'}</span>
            <span>{inv.lineCount} line{inv.lineCount === 1 ? '' : 's'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ImportResultPanel({ result, errors, onClose }) {
  const [openSection, setOpenSection] = useState(null);
  const fmtMoney = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
  };
  const fmtPct = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return '';
    const sign = n > 0 ? '+' : '';
    return ` (${sign}${n.toFixed(1)}%)`;
  };
  const Section = ({ id, label, count, color, bg, border, items, render }) => {
    const isOpen = openSection === id;
    const disabled = !count;
    return (
      <div style={{ marginTop: '0.6rem' }}>
        <button
          type="button"
          onClick={() => !disabled && setOpenSection(isOpen ? null : id)}
          disabled={disabled}
          style={{
            width: '100%', textAlign: 'left', cursor: disabled ? 'default' : 'pointer',
            background: bg, border: `1px solid ${border}`, borderRadius: 8,
            padding: '0.55rem 0.75rem', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', color, fontWeight: 700, fontSize: '0.9rem',
            opacity: disabled ? 0.55 : 1,
          }}
        >
          <span>{label} — {count}</span>
          {!disabled && <span style={{ fontSize: '0.85rem' }}>{isOpen ? '▾' : '▸'}</span>}
        </button>
        {isOpen && count > 0 && (
          <div style={{
            border: `1px solid ${border}`, borderTop: 'none',
            borderRadius: '0 0 8px 8px', background: '#fff',
            padding: '0.5rem 0.75rem', maxHeight: 220, overflowY: 'auto',
          }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.85rem', color: '#3F352E' }}>
              {items.map((it, i) => (
                <li key={i} style={{
                  padding: '0.3rem 0',
                  borderBottom: i < items.length - 1 ? '1px dashed #EEE6DC' : 'none',
                }}>
                  {render(it)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const total = (result.updated || 0) + (result.added || 0) + (result.ignored || 0) + (result.rejected || 0);
  return (
    <div style={{
      background: '#FFFDF9', border: '1px solid #E8DBCE', borderRadius: 12,
      padding: '0.9rem 1rem', marginBottom: '1rem', color: '#3F352E',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 800, fontFamily: "'Montserrat', sans-serif", fontSize: '1.05rem', color: '#5A4A42' }}>
            ✓ Import complete
          </div>
          <div style={{ marginTop: '0.2rem', fontSize: '0.85rem', color: '#7A6A60' }}>
            Processed {total} item{total === 1 ? '' : 's'} from this invoice. Click any section to see exactly what changed.
          </div>
          {result.rejected > 0 && (
            <div style={{ marginTop: '0.2rem', fontSize: '0.85rem', color: '#B71C1C', fontWeight: 600 }}>
              {result.rejected} line{result.rejected === 1 ? ' was' : 's were'} disapproved and left out of your catalog.
            </div>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: '#fff', border: '1px solid #E8DBCE', borderRadius: 8,
            padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 700,
            color: '#5A4A42', cursor: 'pointer',
          }}>Done</button>
        )}
      </div>

      <Section
        id="updated" label="Price updates"
        count={result.updated} color="#8B6135"
        bg="#FCF4EA" border="#E8CEAF"
        items={result.updatedDetails || []}
        render={(it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>{it.description}</span>
              {(it.vendor || it.unit) && (
                <span style={{ color: '#7A6A60', fontSize: '0.8rem', marginLeft: '0.4rem' }}>
                  {[it.vendor, it.unit].filter(Boolean).join(' · ')}
                </span>
              )}
            </span>
            <span style={{ color: '#8B6135', whiteSpace: 'nowrap' }}>
              {fmtMoney(it.oldPrice)} → {fmtMoney(it.newPrice)}{fmtPct(it.deltaPct)}
            </span>
          </div>
        )}
      />

      <Section
        id="added" label="New items added"
        count={result.added} color="#456A2F"
        bg="#F1F8EE" border="#CFE3BE"
        items={result.addedDetails || []}
        render={(it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{it.description}</span>
            <span style={{ color: '#7A6A60', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {[it.vendor, it.unit].filter(Boolean).join(' · ')}{it.case_price != null ? ` · ${fmtMoney(it.case_price)}` : ''}
            </span>
          </div>
        )}
      />

      <Section
        id="skipped" label="Skipped (already in catalog, no change)"
        count={result.ignored} color="#5A4A42"
        bg="#F4EFE7" border="#E1D4C2"
        items={result.skippedDetails || []}
        render={(it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>
              <span style={{ fontWeight: 600 }}>{it.description}</span>
              {it.fuzzy && (
                <span style={{
                  marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 700,
                  color: '#8B6135', background: '#FCF4EA',
                  border: '1px solid #E8CEAF', borderRadius: 4, padding: '1px 5px',
                }}>fuzzy match</span>
              )}
            </span>
            <span style={{ color: '#7A6A60', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {[it.vendor, it.unit].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
      />

      {errors && errors.length > 0 && (
        <div style={{
          marginTop: '0.7rem', background: '#FEEAEA', border: '1px solid #F4C2C2',
          borderRadius: 8, padding: '0.5rem 0.75rem', color: '#A03030', fontSize: '0.85rem',
        }}>
          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{errors.length} error{errors.length === 1 ? '' : 's'}:</div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function UpdatePrices() {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [currentStore, setCurrentStore] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [actionMap, setActionMap] = useState({});
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyErrors, setApplyErrors] = useState([]);
  const [approvalItems, setApprovalItems] = useState([]);
  // Purchase options for the catalog items this batch matched, keyed by item
  // id. Feeds the review table's "how it will land" sentence ("Sugar will have
  // 4 vendors"). Non-fatal: without it the sentence simply omits the count.
  const [matchedOptionsByItemId, setMatchedOptionsByItemId] = useState({});
  const [countTargetsMap, setCountTargetsMap] = useState({});
  const [showApproval, setShowApproval] = useState(false);
  const [aiBlocked, setAiBlocked] = useState(true); // fail-closed until subscription status resolves
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  ));
  // Prep-card context: set when navigated from a pre-count prep card via ?template=<id>
  // Only used to write the prep_price_update_signal localStorage key on successful apply.
  const [prepTemplateId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('template') || null;
  });

  // Whether the AI pricing pass is available. checkAccess owns the rule — do
  // NOT re-derive it from status here, or this banner and the gate disagree (a
  // trial has the same access a setup user and a paid user do). checkAccess and
  // not gatePage: this runs on load, and a page load must not raise a paywall.
  const checkAndSetAiBlocked = async (store) => {
    const storeId = store?.id || store?.store_id;
    if (!storeId) return;
    const gate = window.SubscriptionGate;
    if (!gate) return;
    try {
      const access = await gate.checkAccess(storeId, 'ai_pricing');
      setAiBlocked(access?.allowed === false);
    } catch (_) { setAiBlocked(false); }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) { mq.addEventListener('change', handler); return () => mq.removeEventListener('change', handler); }
    mq.addListener(handler); return () => mq.removeListener(handler);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('currentStore');
    if (stored) {
      try {
        const store = JSON.parse(stored);
        setCurrentStore(store);
        // Check subscription: trial/free users cannot use AI pricing
        if (window.SubscriptionGate) {
          checkAndSetAiBlocked(store);
        } else {
          // SubscriptionGate may not be loaded yet; retry after a short delay
          const retry = setTimeout(() => checkAndSetAiBlocked(store), 1500);
          return () => clearTimeout(retry);
        }
      } catch (_) {}
    }
  }, []);


  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    if (!arr.length) return;
    setSelectedFiles(prev => {
      const sigs = new Set(prev.map(fileSignature));
      const next = [...prev];
      arr.forEach(f => {
        const sig = fileSignature(f);
        if (sigs.has(sig) || !isAccepted(f) || f.size > MAX_FILE_SIZE || next.length >= MAX_FILES) return;
        sigs.add(sig);
        next.push(f);
      });
      return next;
    });
  };

  const removeFile = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  // Take Photo = the shared document scanner (live camera, edge detection,
  // review). Confirmed pages join the same selection — limits, dedupe and
  // approval — as picked files. The native capture input stays as fallback.
  const takePhoto = () => {
    void takeScanPhoto({
      destinationKind: 'price_import',
      stem: 'price',
      onFiles: (files) => { addFiles(files); },
      fallback: () => { cameraInputRef.current?.click(); },
    });
  };

  const [dropFlash, setDropFlash] = useState(null); // null | 'success'

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    // Prefer items API (Chrome/Safari Finder drag), fall back to files
    const dt = e.dataTransfer;
    let files;
    if (dt.items && dt.items.length > 0) {
      files = Array.from(dt.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter(Boolean);
    } else {
      files = Array.from(dt.files || []);
    }
    if (files.length > 0) {
      addFiles(files);
      setDropFlash('success');
      setTimeout(() => setDropFlash(null), 1200);
    }
  }, []);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => {
    // Only clear when leaving the drop zone itself (not a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  }, []);

  const handleProcess = async () => {
    if (!selectedFiles.length || !(currentStore?.id || currentStore?.store_id)) {
      if (!currentStore?.id && !currentStore?.store_id) alert('No store selected. Please log in and select a store.');
      return;
    }

    // Synchronous AI block decision — never rely on React aiBlocked state here
    // Fail closed: if gate unavailable, block AI usage
    const storeId = currentStore?.id || currentStore?.store_id;
    if (!window.SubscriptionGate) {
      return;
    }
    let blockedNow = true; // default closed
    try {
      // gatePage both decides and raises the right prompt (a free store past
      // setup is offered the trial, a lapsed one is asked to renew).
      blockedNow = !(await window.SubscriptionGate.gatePage('ai_pricing'));
      setAiBlocked(blockedNow);
    } catch (_) {
      blockedNow = true;
      setAiBlocked(true);
    }
    if (blockedNow) return;

    setProcessing(true);
    setMatchResults([]);
    setActionMap({});
    setApplyResult(null);
    setApplyErrors([]);

    try {
      setProcessingStatus('Loading your full catalog…');
      const { data: catalog } = await directQuery('inventory_items', {
        select: 'id,description,vendor,unit,pack_size,case_price,unit_price,category,location,brand',
        eq: { store_id: currentStore.id },
        fetchAll: true,
        pageSize: 500,
      });
      const fullCatalog = catalog || [];
      const allCatalog = fullCatalog;

      const allInvoiceItems = [];
      for (const file of selectedFiles) {
        // Each file is one invoice: tag its lines with that invoice's date and
        // number so the resulting price events are dated by (and traceable to)
        // the right invoice, even when several are imported at once.
        const { items, invoiceVendor, invoiceDate, invoiceNumber } = await extractInvoiceItems(file, setProcessingStatus);
        allInvoiceItems.push(...items.map(it => ({
          ...it,
          _sourceFile: file.name,
          _invoiceVendor: invoiceVendor,
          _invoiceDate: invoiceDate,
          _invoiceNumber: invoiceNumber,
        })));
      }

      if (!allInvoiceItems.length) {
        setProcessingStatus('No items found in the uploaded documents.');
        setProcessing(false);
        return;
      }

      setProcessingStatus(`Found ${allInvoiceItems.length} invoice item(s). Matching against ${allCatalog.length} catalog items…`);

      const catalogById = new Map(allCatalog.map(c => [String(c.id), c]));
      const results = [];

      for (let i = 0; i < allInvoiceItems.length; i++) {
        const invoiceItem = allInvoiceItems[i];
        setProcessingStatus(`Matching item ${i + 1} of ${allInvoiceItems.length}: ${invoiceItem.name || '?'}…`);

        const candidates = getCandidates(invoiceItem, allCatalog, CANDIDATE_LIMIT);
        const match = await callEdgeMatch(invoiceItem, candidates);

        const catalogMatch = match.catalogId ? (catalogById.get(String(match.catalogId)) || null) : null;
        const suggestedAction = (() => {
          const raw = match.suggestedAction || 'add_new';
          if ((raw === ACTIONS.UPDATE_ALL || raw === ACTIONS.UPDATE_PRICE) && !catalogMatch) return ACTIONS.ADD_NEW;
          return raw;
        })();

        // Top-scored candidates, kept on the row so the review can offer a
        // compact "match: … ▸ change" switcher (pin a different catalog item,
        // or force the line in as new) without re-running the AI match.
        const scoredCandidates = candidates.slice(0, 5)
          .map((item) => ({ item, score: scoreCandidacy(invoiceItem, item) }))
          .filter((c) => c.score > 0);

        results.push({
          invoiceItem,
          catalogMatch,
          confidence: match.confidence || 'none',
          suggestedAction,
          reason: match.reason || '',
          candidates: scoredCandidates,
        });
      }

      const initialActions = {};
      results.forEach((r, i) => { initialActions[i] = r.suggestedAction; });

      setMatchResults(results);
      setActionMap(initialActions);
      setProcessingStatus(`Matched ${results.length} item(s). Building approval list…`);

      // ── Transform to ImportApprovalTable format ──────────────────────
      // Use deterministic name+vendor+unit key dedup first; fall back to
      // AI suggestion only when no exact key match is found in the catalog.
      const storeIdForApproval = currentStore?.id || currentStore?.store_id;
      const catalogLookup = buildCatalogLookup(allCatalog);
      const approval = [];
      const targetsMap = {};
      let tempIdx = 0;

      for (const r of results) {
        const { invoiceItem, catalogMatch, suggestedAction } = r;

        // Build a synthetic item shaped like importDedup expects
        const syntheticItem = {
          description: invoiceItem.name || '',
          vendor: invoiceItem.vendor || '',
          unit: invoiceItem.unit || '',
          case_price: invoiceItem.price,
        };

        // Deterministic dedup: strict name+vendor+unit key match only.
        // AI fuzzy match result is attached as context but never overrides classification.
        const dedupResult = classifyItem(syntheticItem, catalogLookup);

        let classification = dedupResult.classification; // 'unchanged' | 'price_change' | 'new'
        let existingItem = dedupResult.existingItem;

        const itemId = (classification !== 'new' && existingItem?.id)
          ? String(existingItem.id)
          : `_temp_${tempIdx++}`;

        const oldPrice = existingItem?.case_price != null ? parseFloat(existingItem.case_price) : null;
        const newPrice = invoiceItem.price != null ? parseFloat(invoiceItem.price) : null;

        // Use dedup-computed deltas for exact matches; compute from AI match otherwise
        const deltaAbs = dedupResult.priceDeltaAbsolute ??
          ((oldPrice != null && newPrice != null) ? +(newPrice - oldPrice).toFixed(4) : null);
        const deltaPct = dedupResult.priceDeltaPct ??
          ((deltaAbs != null && oldPrice != null && oldPrice !== 0)
            ? +((deltaAbs / Math.abs(oldPrice)) * 100).toFixed(2)
            : null);

        const approvalItem = {
          id: itemId,
          description: invoiceItem.name || existingItem?.description || 'Unknown Item',
          vendor: invoiceItem.vendor || existingItem?.vendor || null,
          unit: invoiceItem.unit || existingItem?.unit || null,
          case_price: newPrice ?? oldPrice ?? 0,
          category: existingItem?.category || null,
          location: existingItem?.location || null,
          _classification: classification,
          _existingItem: existingItem,
          _priceDeltaAbsolute: deltaAbs,
          _priceDeltaPct: deltaPct,
          _invoiceItem: invoiceItem,
          _sourceFile: invoiceItem._sourceFile ?? null,
          _invoiceVendor: invoiceItem._invoiceVendor ?? null,
          _invoiceDate: invoiceItem._invoiceDate ?? null,
          _invoiceNumber: invoiceItem._invoiceNumber ?? null,
        };
        approval.push(approvalItem);

        if (classification === 'new' && storeIdForApproval) {
          try {
            const targets = await findCountTargetsForNewItem({
              storeId: storeIdForApproval,
              itemCategory: null,
              itemVendor: invoiceItem.vendor || null,
              inventoryItemId: null,
              itemSnapshot: approvalItem,
            });
            targetsMap[itemId] = targets.map((t) => ({ ...t, selected: !t.alreadyPresent }));
          } catch (_) {
            targetsMap[itemId] = [];
          }
        }
      }

      // How many vendors each matched item already has, so the review can say
      // "Sugar will have 4 vendors" rather than leaving the consequence implied.
      try {
        const matchedIds = [...new Set(
          approval.map((row) => row._existingItem?.id).filter((id) => id != null),
        )];
        setMatchedOptionsByItemId(matchedIds.length
          ? await fetchOptionsForItems(storeId, matchedIds)
          : {});
      } catch (optionsErr) {
        console.warn('[UpdatePrices] Purchase options unavailable (non-fatal):', optionsErr?.message);
        setMatchedOptionsByItemId({});
      }

      setApprovalItems(approval);
      setCountTargetsMap(targetsMap);
      setShowApproval(true);
      setProcessingStatus(`${approval.length} item(s) ready for approval.`);
    } catch (err) {
      console.error('Price update error:', err);
      setProcessingStatus(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const setAction = (idx, action) => setActionMap(prev => ({ ...prev, [idx]: action }));

  // Pin a different catalog match on a review row, or clear the match to force
  // the line in as a new item. Uses the same actionMap the review already
  // honors: a pinned candidate acts as a price update, no match means add new.
  const setRowMatch = (idx, catalogItem) => {
    setMatchResults((prev) => prev.map((r, i) => (i === idx ? { ...r, catalogMatch: catalogItem || null } : r)));
    setAction(idx, catalogItem ? ACTIONS.UPDATE_PRICE : ACTIONS.ADD_NEW);
  };

  // User correction from the invoice header: apply an edited vendor and/or date
  // to every line of that invoice. Vendor writes through to the per-line vendor
  // too, so the approval table, the recorded invoice_vendor, and any new items
  // all agree; date sets each line's price-change date (changed_at) on save.
  const editInvoiceFields = (group, patch) => {
    setApprovalItems((prev) => applyInvoiceFieldEdit(prev, group, patch));
  };

  const applyBulkAction = (action) => {
    const next = {};
    matchResults.forEach((r, i) => {
      const { catalogMatch } = r;
      if ((action === ACTIONS.UPDATE_ALL || action === ACTIONS.UPDATE_PRICE) && !catalogMatch) {
        next[i] = ACTIONS.ADD_NEW;
      } else {
        next[i] = action;
      }
    });
    setActionMap(next);
  };

  const handleApply = async (itemsToApply) => {
    const storeId = currentStore?.id || currentStore?.store_id;
    if (!storeId) return;
    const reviewedItems = Array.isArray(itemsToApply) ? itemsToApply : approvalItems;
    if (!reviewedItems.length) return;

    setApplying(true);
    setApplyResult(null);
    setApplyErrors([]);

    // When this import ran. Recorded on each price event (import_date) for the
    // audit trail; the event itself is dated by the invoice date, not this.
    const importedAt = new Date().toISOString();

    // ── Re-run dedup at save-time on current (possibly edited) field values ──
    let liveItems = reviewedItems;
    try {
      const { data: catData } = await directQuery('inventory_items', {
        select: 'id,description,vendor,unit,case_price,unit_price',
        eq: { store_id: storeId },
        fetchAll: true,
        pageSize: 500,
      });
      const liveCatalog = catData || [];
      const liveCatalogById = new Map(liveCatalog.map((r) => [String(r.id), r]));
      const freshLookup = buildCatalogLookup(liveCatalog);

      liveItems = reviewedItems.map((item) => {
        const synth = {
          description: item.description,
          vendor: item.vendor,
          unit: item.unit,
          case_price: item.case_price,
        };
        const result = classifyItem(synth, freshLookup);
        const freshExisting = result.existingItem
          ? (liveCatalogById.get(String(result.existingItem.id)) || result.existingItem)
          : null;
        // Honor a user-driven override; otherwise use the fresh dedup result.
        const finalClass = item._classificationOverride || result.classification;
        return {
          ...item,
          _classification: finalClass,
          _existingItem: freshExisting,
          _priceDeltaAbsolute: result.priceDeltaAbsolute,
          _priceDeltaPct: result.priceDeltaPct,
        };
      });
    } catch (reclassErr) {
      console.warn('[UpdatePrices] Save-time reclassification failed, using review values:', reclassErr);
    }

    const items = liveItems;

    // Confirm any override-induced duplicates before proceeding.
    const duplicateOverrides = items.filter(
      (i) => i._classificationOverride === 'new' && i._existingItem
    );
    if (duplicateOverrides.length > 0) {
      const lines = duplicateOverrides
        .slice(0, 10)
        .map((i) => `  • ${i.description || '(unnamed)'} — duplicates "${i._existingItem.description}"`)
        .join('\n');
      const more = duplicateOverrides.length > 10 ? `\n  …and ${duplicateOverrides.length - 10} more` : '';
      const ok = window.confirm(
        `${duplicateOverrides.length} item${duplicateOverrides.length === 1 ? '' : 's'} will be saved as NEW even though a matching catalog item already exists. This will create duplicate rows in your catalog:\n\n${lines}${more}\n\nContinue?`
      );
      if (!ok) {
        setApplying(false);
        return;
      }
    }

    let updated = 0;
    let added = 0;
    let ignored = 0;
    let rejected = 0;
    const errors = [];
    const skippedDetails = [];
    const updatedDetails = [];
    const addedDetails = [];

    for (const item of items) {
      try {
        const classification = item._classification || 'unchanged';

        if (classification === 'unchanged') {
          ignored++;
          skippedDetails.push({
            description: item.description || item._existingItem?.description || '(unnamed)',
            vendor: item.vendor || item._existingItem?.vendor || '',
            unit: item.unit || item._existingItem?.unit || '',
            case_price: parseFloat(item.case_price) || parseFloat(item._existingItem?.case_price) || null,
            fuzzy: !!item._fuzzyMatched,
          });
          continue;
        }

        if (classification === CLASSIFICATIONS.REJECTED) {
          // Explicitly disapproved in review. Nothing is written, and it is
          // reported apart from "unchanged" so the count is honest.
          rejected++;
          continue;
        }

        if (classification === CLASSIFICATIONS.NEW_OPTION) {
          // The line matched an item we already count, but it is a different
          // vendor's product. It becomes another way to buy that item —
          // no second countable item, and the item's other vendor prices are
          // left exactly as they were.
          const existing = item._existingItem;
          if (!existing?.id) { ignored++; continue; }
          const price = parseFloat(item.case_price);
          if (!Number.isFinite(price) || price <= 0) { ignored++; continue; }

          const option = await writeVendorProduct({
            storeId,
            inventoryItemId: existing.id,
            item,
            effectiveDate: item._invoiceDate || importedAt,
          });
          added++;
          addedDetails.push({
            description: `${existing.description || item.description || '(unnamed)'} — new vendor product`,
            vendor: item.vendor || '',
            unit: item.unit || existing.unit || '',
            case_price: price,
          });
          if (option?.id) {
            await recordPriceHistory({
              inventoryItemId: existing.id,
              storeId,
              oldName: null,
              newName: existing.description || null,
              oldCasePrice: null,
              newCasePrice: price,
              oldUnitPrice: null,
              newUnitPrice: null,
              source: 'ai_price_import',
              invoiceVendor: item.vendor || null,
              invoiceUnit: item.unit || null,
              invoiceDate: item._invoiceDate || null,
              invoiceNumber: item._invoiceNumber || null,
              importDate: importedAt,
              purchaseOptionId: option.id,
            });
          }
          continue;
        }

        if (classification === 'price_change') {
          const existing = item._existingItem;
          if (!existing?.id) { ignored++; continue; }
          const newPrice = parseFloat(item.case_price);
          if (!Number.isFinite(newPrice)) { ignored++; continue; }

          const updates = { case_price: newPrice };
          if (existing.unit_price != null) updates.unit_price = newPrice;

          const { error } = await directUpdate('inventory_items', updates, {
            id: existing.id,
            store_id: storeId,
          });
          if (error) throw error;

          // The price belongs to a vendor's product, not to the item in the
          // abstract: record it there too so a second supplier adds a way to
          // buy rather than overwriting the first supplier's price.
          const changedOption = await writeVendorProduct({
            storeId,
            inventoryItemId: existing.id,
            item,
            effectiveDate: item._invoiceDate || importedAt,
          });

          await recordPriceHistory({
            inventoryItemId: existing.id,
            storeId,
            oldName: existing.description,
            newName: item.description || existing.description,
            oldCasePrice: existing.case_price,
            newCasePrice: newPrice,
            oldUnitPrice: existing.unit_price,
            newUnitPrice: existing.unit_price != null ? newPrice : null,
            source: 'ai_price_import',
            purchaseOptionId: changedOption?.id || null,
            normalizedUnitCost: changedOption?.normalized_unit_cost ?? null,
            effectiveDate: item._invoiceDate || null,
            invoiceVendor: item.vendor || null,
            invoiceUnit: item.unit || null,
            priceDeltaAbsolute: item._priceDeltaAbsolute,
            priceDeltaPct: item._priceDeltaPct,
            invoiceDate: item._invoiceDate || null,
            invoiceNumber: item._invoiceNumber || null,
            importDate: importedAt,
          });
          updated++;
          updatedDetails.push({
            description: item.description || existing.description || '(unnamed)',
            vendor: item.vendor || existing.vendor || '',
            unit: item.unit || existing.unit || '',
            oldPrice: parseFloat(existing.case_price),
            newPrice,
            deltaAbs: item._priceDeltaAbsolute,
            deltaPct: item._priceDeltaPct,
          });
        } else if (classification === 'new') {
          const { data: inserted, error } = await supabase.from('inventory_items').insert([{
            store_id: storeId,
            description: String(item.description || '').trim() || 'Unknown Item',
            vendor: item.vendor || null,
            unit: item.unit || null,
            case_price: parseFloat(item.case_price) || null,
            category: item.category || null,
            location: item.location || null,
          }]).select().single();
          if (error) throw error;

          // Auto-assign to selected count sessions
          if (inserted?.id) {
            const targets = (countTargetsMap[item.id] || []).filter((t) => t.selected);
            if (targets.length) {
              const snapshot = {
                id: inserted.id,
                description: inserted.description,
                category: inserted.category,
                location: inserted.location,
                vendor: inserted.vendor,
                unit: inserted.unit,
                case_price: inserted.case_price,
              };
              await assignItemToCountSessions({
                storeId,
                inventoryItemId: inserted.id,
                itemSnapshot: snapshot,
                selectedSessions: targets.map((t) => t.session),
              });
            }
          }
          // Default price handling for a newly discovered item: its first way
          // to buy it, so the item starts life inside the multi-vendor model
          // instead of carrying a bare price that a later supplier would
          // silently overwrite.
          const firstOption = await writeVendorProduct({
            storeId,
            inventoryItemId: inserted?.id,
            item,
            effectiveDate: item._invoiceDate || importedAt,
          });

          const insertedPrice = parseFloat(inserted.case_price);
          if (Number.isFinite(insertedPrice) && insertedPrice > 0) {
            await recordPriceHistory({
              inventoryItemId: inserted.id,
              storeId,
              oldName: null,
              newName: inserted.description,
              oldCasePrice: null,
              newCasePrice: insertedPrice,
              oldUnitPrice: null,
              newUnitPrice: null,
              source: 'ai_price_import',
              purchaseOptionId: firstOption?.id || null,
              normalizedUnitCost: firstOption?.normalized_unit_cost ?? null,
              effectiveDate: item._invoiceDate || null,
              invoiceVendor: item.vendor || null,
              invoiceUnit: item.unit || null,
              invoiceDate: item._invoiceDate || null,
              invoiceNumber: item._invoiceNumber || null,
              importDate: importedAt,
            });
          }
          added++;
          addedDetails.push({
            description: item.description || '(unnamed)',
            vendor: item.vendor || '',
            unit: item.unit || '',
            case_price: parseFloat(item.case_price) || null,
          });
        }
      } catch (err) {
        errors.push(`${item.description || '?'}: ${err.message}`);
      }
    }

    setApplyResult({ updated, added, ignored, rejected, skippedDetails, updatedDetails, addedDetails });
    setApplyErrors(errors);
    setApplying(false);
    // Note: keep the approval screen mounted so the user sees the per-item
    // summary panel below the (now-empty) review table. They can hit "Back"
    // when done. Clear the row data to make it obvious nothing is pending.
    if (errors.length === 0) {
      setApprovalItems([]);
      setCountTargetsMap({});
      setMatchResults([]);
      // Direct completion signal for pre-count prep cards: when navigated from a prep card
      // via ?template=<id>, write a localStorage key that TaskAlertDashboard reads on next load
      // to mark update_prices_completed_at without relying solely on price_history polling.
      if (prepTemplateId && storeId) {
        try {
          localStorage.setItem('prep_price_update_signal', JSON.stringify({
            templateId: String(prepTemplateId),
            storeId: String(storeId),
            completedAt: new Date().toISOString(),
          }));
        } catch (_) {}
      }
    }
  };

  const confidenceBadge = (conf) => {
    const colors = { high: '#6B8E6B', medium: '#B8860B', low: '#CD5C5C', none: '#8B7B73' };
    const labels = { high: 'High', medium: 'Medium', low: 'Low', none: 'No match' };
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        fontSize: '0.72rem', fontWeight: 600, color: '#fff',
        background: colors[conf] || '#8B7B73', marginLeft: '0.4rem',
      }}>
        {labels[conf] || conf}
      </span>
    );
  };

  const fmt = (val) => {
    const n = parseFloat(val);
    return (val == null || isNaN(n)) ? '—' : `$${n.toFixed(2)}`;
  };

  const pendingCount = Object.values(actionMap).filter(a => a !== ACTIONS.IGNORE).length;

  // ── Approval screen (replaces old match cards review) ─────────────────
  if (showApproval) {
    return (
      <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
        {applyResult && (
          <ImportResultPanel
            result={applyResult}
            errors={applyErrors}
            onClose={() => {
              setApplyResult(null);
              setApplyErrors([]);
              setShowApproval(false);
            }}
          />
        )}
        <InvoiceSummaryBanner items={approvalItems} onEditInvoice={editInvoiceFields} />
        <ImportApprovalTable
          items={approvalItems}
          onUpdateItem={(itemId, field, value) => {
            setApprovalItems((prev) =>
              prev.map((it) => {
                if (it.id !== itemId) return it;
                const updated = { ...it, [field]: value };
                // Skip automatic classification flip if the user has manually overridden it.
                if (field === 'case_price' && it._existingItem && !it._classificationOverride) {
                  const oldP = parseFloat(it._existingItem.case_price);
                  const newP = parseFloat(value);
                  if (Number.isFinite(oldP) && Number.isFinite(newP)) {
                    const samePrice = Math.abs(newP - oldP) < 0.001;
                    if (samePrice) {
                      updated._classification = 'unchanged';
                      updated._priceDeltaAbsolute = null;
                      updated._priceDeltaPct = null;
                    } else {
                      updated._classification = 'price_change';
                      updated._priceDeltaAbsolute = +(newP - oldP).toFixed(4);
                      updated._priceDeltaPct = oldP !== 0 ? +((newP - oldP) / Math.abs(oldP) * 100).toFixed(2) : null;
                    }
                  }
                }
                return updated;
              })
            );
          }}
          onReclassifyItem={(itemId, newClassification) => {
            setApprovalItems((prev) =>
              prev.map((it) => (it.id === itemId ? overrideItemClassification(it, newClassification) : it))
            );
          }}
          onImport={handleApply}
          onBack={() => {
            setShowApproval(false);
            setApprovalItems([]);
            setCountTargetsMap({});
          }}
          isImporting={applying}
          storeId={currentStore?.id || currentStore?.store_id}
          // This flow's save path writes purchase_options, so a line may land
          // as another way to buy an item we already count.
          allowVendorProduct
          landingContextFor={(row) => {
            const matchedId = row?._existingItem?.id;
            const options = matchedId != null ? (matchedOptionsByItemId[matchedId] || []) : [];
            return {
              existingVendorCount: distinctVendorCount(activeOptions(options)),
              existingOptionCount: activeOptions(options).length,
              countUnit: row?._existingItem?.unit || row?.unit || null,
            };
          }}
          countTargetsMap={countTargetsMap}
          onToggleCountTarget={(itemId, sessionId) => {
            setCountTargetsMap((prev) => {
              const targets = (prev[itemId] || []).map((t) =>
                t.session.id === sessionId ? { ...t, selected: !t.selected } : t
              );
              return { ...prev, [itemId]: targets };
            });
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Open Sans', sans-serif" }}>

      {/* Upload Section */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #FAF6F2',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '1.5rem', marginBottom: '1.5rem',
      }}>
        <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#5A4A42', marginBottom: '0.5rem', fontSize: '1.25rem' }}>
          Upload Invoice
        </h3>
        <p style={{ color: '#8B7B73', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Upload supplier invoices or price lists. Countertop reads each document, identifies the items and prices,
          and prepares your catalog updates for review.
          Accepted formats: JPG, PNG, WEBP, HEIC, PDF, CSV, Excel.
        </p>


        {/* Trial AI Block Banner */}
        {aiBlocked && (
          <div style={{
            background: '#FFF3E0', border: '1px solid #FFB300', borderRadius: 10,
            padding: '1rem 1.25rem', marginBottom: '1rem', textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.35rem' }}>🔒</div>
            {isIosShell() ? (
              <p style={{ color: '#5A4A42', fontWeight: 600, margin: 0, fontSize: '0.95rem' }}>
                This feature is not available to your store. Please contact the account owner.
              </p>
            ) : (
              <>
                {/* Trials are not blocked here any more, so this banner only
                    ever greets a plan that genuinely lacks AI pricing. */}
                <p style={{ color: '#5A4A42', fontWeight: 600, margin: '0 0 0.75rem', fontSize: '0.95rem' }}>
                  AI pricing is not included on your current plan.
                </p>
                <button
                  onClick={() => { window.location.href = 'subscribe.html'; }}
                  style={{
                    padding: '0.6rem 1.5rem', background: '#2e7d32', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
                  }}>
                  Subscriptions
                </button>
              </>
            )}
          </div>
        )}

        <input ref={fileInputRef} type="file" multiple
          accept=".jpg,.jpeg,.png,.webp,.heic,.pdf,.csv,.xlsx,.xls"
          style={visuallyHiddenFileInputStyle} tabIndex={-1} aria-hidden="true"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
          style={visuallyHiddenFileInputStyle} tabIndex={-1} aria-hidden="true"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

        {isMobile ? (
          /* Mobile: compact upload zone with prominent action buttons */
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              border: `2px dashed ${dropFlash ? '#28a745' : '#D4C4B0'}`,
              borderRadius: 10, padding: '1rem', textAlign: 'center',
              background: dropFlash ? '#e8f5e9' : '#FEFCFA', marginBottom: '0.75rem',
            }}>
              {dropFlash ? (
                <p style={{ color: '#28a745', fontWeight: 700, margin: 0 }}>✅ File added!</p>
              ) : (
                <p style={{ color: '#8B7B73', fontSize: '0.82rem', margin: 0 }}>
                  Accepted: JPG, PNG, HEIC, PDF, CSV, Excel · Max 10 MB
                </p>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <button onClick={takePhoto}
                style={{
                  padding: '0.85rem 0.75rem', border: '2px solid #5A4A42', borderRadius: 10,
                  background: '#5A4A42', color: '#fff', fontWeight: 700, cursor: 'pointer',
                  fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                }}>
                📷 Take Photo
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '0.85rem 0.75rem', border: '2px solid #D4C4B0', borderRadius: 10,
                  background: '#fff', color: '#5A4A42', fontWeight: 700, cursor: 'pointer',
                  fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                }}>
                📂 Browse Files
              </button>
            </div>
          </div>
        ) : (
          /* Desktop: drag-and-drop zone + buttons */
          <>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onClick={(e) => { if (!e.defaultPrevented) fileInputRef.current?.click(); }}
              style={{
                border: `2px dashed ${dropFlash ? '#28a745' : isDragOver ? '#5A4A42' : '#D4C4B0'}`,
                borderRadius: 10, padding: '2rem 1rem', textAlign: 'center',
                cursor: 'pointer',
                background: dropFlash ? '#e8f5e9' : isDragOver ? '#FAF6F2' : '#FEFCFA',
                transition: 'all 0.25s', marginBottom: '1rem',
              }}
            >
              {dropFlash ? (
                <>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                  <p style={{ color: '#28a745', fontWeight: 700, margin: 0, fontSize: '1rem' }}>File added!</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{isDragOver ? '📥' : '📁'}</div>
                  <p style={{ color: '#5A4A42', fontWeight: 600, margin: 0 }}>{isDragOver ? 'Release to add files' : 'Drop files here or click to browse'}</p>
                  <p style={{ color: '#8B7B73', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                    Max {MAX_FILES} files · 10 MB each
                  </p>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <button onClick={() => fileInputRef.current?.click()}
                style={{ padding: '0.5rem 1rem', border: '1px solid #D4C4B0', borderRadius: 8, background: '#fff', color: '#5A4A42', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                📂 Browse Files
              </button>
              <button onClick={takePhoto}
                style={{ padding: '0.5rem 1rem', border: '1px solid #D4C4B0', borderRadius: 8, background: '#fff', color: '#5A4A42', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                📷 Take Photo
              </button>
            </div>
          </>
        )}

        {selectedFiles.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            {selectedFiles.map((f, i) => (
              <div key={i}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.4rem 0.75rem', background: '#FAF6F2', borderRadius: 8,
                  marginBottom: '0.25rem', fontSize: '0.85rem',
                }}>
                  <span style={{ color: '#5A4A42', fontWeight: 500 }}>
                    {isImage(f) ? '🖼️' : f.name.endsWith('.pdf') ? '📄' : '📊'} {f.name}
                  </span>
                  <button onClick={() => removeFile(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7B73', fontSize: '1rem' }}>✕</button>
                </div>
                <StagedFilePreview file={f} />
              </div>
            ))}
          </div>
        )}

        <button onClick={aiBlocked ? undefined : handleProcess} disabled={processing || !selectedFiles.length || aiBlocked}
          title={aiBlocked ? 'AI pricing is not included on your current plan' : undefined}
          style={{
            padding: '0.65rem 2rem', border: 'none', borderRadius: 8, fontWeight: 700,
            fontSize: '0.95rem', cursor: (processing || !selectedFiles.length || aiBlocked) ? 'not-allowed' : 'pointer',
            background: (processing || !selectedFiles.length || aiBlocked) ? '#D4C4B0' : '#5A4A42', color: '#fff',
          }}>
          {processing ? '⏳ Processing…' : '✨ Extract & Match Prices'}
        </button>

        {processingStatus && (
          <p style={{ color: '#5A4A42', fontSize: '0.85rem', marginTop: '0.75rem', fontStyle: processing ? 'italic' : 'normal' }}>
            {processingStatus}
          </p>
        )}
      </div>

      {/* Match Review */}
      {matchResults.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #FAF6F2',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '1.5rem', marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#5A4A42', margin: 0, fontSize: '1.25rem' }}>
              Review Matches ({matchResults.length} items)
            </h3>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.78rem', color: '#8B7B73' }}>Apply to all:</span>
              {Object.entries(ACTION_LABELS).map(([action, label]) => (
                <button key={action} onClick={() => applyBulkAction(action)}
                  style={{
                    padding: '0.28rem 0.6rem', border: `1px solid ${ACTION_COLORS[action]}`, borderRadius: 6,
                    background: '#fff', color: ACTION_COLORS[action], fontWeight: 600, cursor: 'pointer', fontSize: '0.73rem',
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {matchResults.map((result, idx) => {
            const { invoiceItem, catalogMatch, confidence, reason, candidates = [] } = result;
            const currentAction = actionMap[idx] || ACTIONS.IGNORE;
            // Options for the "match: … ▸ change" switcher. The current match
            // always appears even when it fell outside the scored shortlist.
            const matchOptions = [...candidates];
            if (catalogMatch && !matchOptions.some((c) => String(c.item.id) === String(catalogMatch.id))) {
              matchOptions.unshift({ item: catalogMatch, score: null });
            }
            return (
              <div key={idx} style={{
                border: '1px solid #F0E8E0', borderRadius: 10, padding: '0.75rem',
                marginBottom: '0.6rem', background: '#FEFCFA', overflow: 'hidden',
              }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.6rem', alignItems: 'flex-start', marginBottom: '0.5rem', minWidth: 0 }}>
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    {catalogMatch ? (
                      <>
                        {/* Canonical-first: the existing catalog item leads; the
                            imported invoice text stays visible, muted, beneath. */}
                        <div style={{ fontSize: '0.62rem', color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.15rem' }}>Matched Item</div>
                        <div style={{ fontWeight: 700, color: '#5A4A42', fontSize: '0.82rem', wordBreak: 'break-word' }}>{catalogMatch.description}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8B7B73', wordBreak: 'break-word' }}>
                          from invoice: {[invoiceItem.name, invoiceItem.vendor, invoiceItem.unit].filter(Boolean).join(' · ') || '—'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#5A4A42', fontWeight: 600 }}>{fmt(invoiceItem.price)}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.62rem', color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.15rem' }}>Invoice Item</div>
                        <div style={{ fontWeight: 700, color: '#5A4A42', fontSize: '0.82rem', wordBreak: 'break-word' }}>{invoiceItem.name || '—'}</div>
                        <div style={{ fontSize: '0.72rem', color: '#8B7B73', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {invoiceItem.vendor || 'No vendor'} · {invoiceItem.unit || 'no unit'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#5A4A42', fontWeight: 600 }}>{fmt(invoiceItem.price)}</div>
                      </>
                    )}
                  </div>

                  {!isMobile && <div style={{ flexShrink: 0, color: '#C8B5A0', fontSize: '1rem', paddingTop: '1.1rem' }}>→</div>}
                  {isMobile && <div style={{ fontSize: '0.62rem', color: '#C8B5A0', fontWeight: 700, letterSpacing: '0.04em' }}>↓ MATCHED TO</div>}

                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div style={{ fontSize: '0.62rem', color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                      Catalog Match{confidenceBadge(confidence)}
                    </div>
                    {catalogMatch ? (
                      <>
                        <div style={{ fontSize: '0.72rem', color: '#8B7B73', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {catalogMatch.vendor || 'No vendor'} · {catalogMatch.unit || catalogMatch.pack_size || 'no unit'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#8B7B73' }}>
                          Current: {fmt(catalogMatch.case_price ?? catalogMatch.unit_price)}
                        </div>
                        {reason && (
                          <div style={{ fontSize: '0.67rem', color: '#A09080', marginTop: '0.15rem', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {reason}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: '#8B7B73', fontSize: '0.78rem', fontStyle: 'italic' }}>No catalog match found</div>
                    )}
                    {matchOptions.length > 0 && (
                      <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', minWidth: 0 }}>
                        <span style={{ fontSize: '0.68rem', color: '#8B7B73', fontWeight: 700, flexShrink: 0 }}>match:</span>
                        <select
                          value={catalogMatch ? String(catalogMatch.id) : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            const next = v ? (matchOptions.find((c) => String(c.item.id) === v)?.item || null) : null;
                            setRowMatch(idx, next);
                          }}
                          aria-label={`Change catalog match for ${invoiceItem.name || 'invoice item'}`}
                          style={{
                            fontSize: '0.72rem', padding: '0.2rem 0.3rem', borderRadius: 6,
                            border: '1px solid #D8C8B8', color: '#5A4A42', background: '#fff',
                            minWidth: 0, maxWidth: '100%',
                          }}
                        >
                          <option value="">No match — add as new item</option>
                          {matchOptions.map((c) => (
                            <option key={String(c.item.id)} value={String(c.item.id)}>
                              {c.item.description}{c.score != null ? ` (${Math.round(c.score * 100)}%)` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', borderTop: '1px solid #F0E8E0', paddingTop: '0.5rem' }}>
                  {Object.entries(ACTION_LABELS).map(([action, label]) => {
                    const isSelected = currentAction === action;
                    const disabled = (action === ACTIONS.UPDATE_ALL || action === ACTIONS.UPDATE_PRICE) && !catalogMatch;
                    return (
                      <button key={action}
                        onClick={() => !disabled && setAction(idx, action)}
                        disabled={disabled}
                        title={disabled ? 'No catalog match — use "Add as New Item" instead' : undefined}
                        style={{
                          padding: '0.22rem 0.55rem', borderRadius: 6, fontWeight: 600,
                          fontSize: '0.7rem', cursor: disabled ? 'not-allowed' : 'pointer',
                          border: isSelected ? 'none' : `1px solid ${disabled ? '#D4C4B0' : ACTION_COLORS[action]}`,
                          background: isSelected ? ACTION_COLORS[action] : '#fff',
                          color: isSelected ? '#fff' : (disabled ? '#D4C4B0' : ACTION_COLORS[action]),
                          opacity: disabled ? 0.45 : 1, whiteSpace: 'nowrap',
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div style={{ borderTop: '1px solid #F0E8E0', paddingTop: '1rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <button onClick={handleApply} disabled={applying}
              style={{
                padding: '0.65rem 2rem', border: 'none', borderRadius: 8, fontWeight: 700,
                fontSize: '0.95rem', cursor: applying ? 'not-allowed' : 'pointer',
                background: applying ? '#D4C4B0' : '#5A4A42', color: '#fff',
              }}>
              {applying ? '⏳ Applying…' : '✅ Apply All Actions'}
            </button>
            <span style={{ fontSize: '0.82rem', color: '#8B7B73' }}>
              {pendingCount} action(s) will be applied
            </span>
          </div>

          {applyResult && (
            <div style={{
              marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: 8,
              background: applyErrors.length ? '#FFF9EC' : '#F0F8F0',
              border: `1px solid ${applyErrors.length ? '#FFE0A0' : '#C0E0C0'}`,
            }}>
              <strong style={{ color: '#5A4A42' }}>
                {applyErrors.length ? '⚠️ Completed with errors' : '✅ Done!'}
              </strong>
              <span style={{ color: '#8B7B73', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                {applyResult.updated} updated · {applyResult.added} added · {applyResult.ignored} ignored
              </span>
              {applyErrors.length > 0 && (
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.8rem', color: '#856404' }}>
                  {applyErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
